/**
 * FmPriceChart — DS line-chart primitive for timber (and future commodity)
 * price-history series.
 *
 * Owns all Recharts internals (LineChart / ResponsiveContainer / Brush / axes /
 * tooltip) so route-owned code never imports recharts or sets chart geometry.
 * It is fully data-driven: the caller passes rows plus declarative series
 * descriptors (per-code sub-lines, overlay lines) and axis/tooltip formatters —
 * no chart geometry, colour hex, or i18n lives here. Missing points render as
 * gaps (NO-FALLBACK): a null datum is never coerced to 0.
 *
 * Migrated from the former route-owned components/timber-prices/TimberPriceChart
 * so the Timber Prices route can be sealed to zero page-purity violations.
 * Chart height + brush dimensions resolve
 * from DS-owned chartGeometry.ts (--fm-chart-* tokens).
 *
 * Overlay stroke geometry (width, opacity, dash) is now DS-owned: semantic
 * overlay variants ('dominant' | 'rolling' | 'secondary') map to geometry
 * inside this primitive. Routes pass only variant names, never raw numbers.
 *
 * Lines are drawn static, like every other chart kind: a draw-in animation
 * starts each line at zero length, so a reader scrolling to the chart, a print
 * or a capture taken as it enters view sees an empty plot.
 */

import React, { useCallback, useMemo } from 'react';
import {
  Brush,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  usePlotArea,
  useXAxisScale,
  useYAxisScale,
} from 'recharts';
import {
  CHART_HEIGHT,
  CHART_BRUSH_HEIGHT,
  CHART_BRUSH_TRAVELLER_WIDTH,
  CHART_MAX_PLOT_WIDTH,
} from './chartGeometry';
import {
  CHART_GRID_PROPS,
  CHART_XAXIS_PROPS,
  CHART_YAXIS_PROPS,
  yAxisTitle,
} from './FmChartChrome.constants';
import { FmChartTooltip } from './FmChartChrome';
import './FmChartFrame.css';
import './FmPriceChart.css';

// Visual constants (DS-owned; never raw literals at call sites)

/** Grid line dash pattern for CartesianGrid. */
/** Stroke width for per-assortment/code lines. */
const FM_PRICE_LINE_STROKE_WIDTH_WEEKLY = 2;
const END_LABEL_OFFSET = 10;
/** Minimum baseline-to-baseline gap so stacked end labels never overprint. */
const END_LABEL_MIN_GAP = 16;
const MAX_END_LABELS = 3;
const NO_CODE_SERIES: FmPriceCodeSeries[] = [];

// Overlay variant geometry (semantic → stroke numbers)
const OVERLAY_VARIANTS = {
  dominant: {
    strokeWidth: 2.5,
    strokeOpacity: undefined,
    dashArray: undefined,
  },
  rolling: {
    strokeWidth: 2.5,
    strokeOpacity: 0.7,
    dashArray: undefined,
  },
  secondary: {
    strokeWidth: 2,
    strokeOpacity: undefined,
    dashArray: '5 5',
  },
} as const;

// Public shapes

/** A single x/value row. `value` fields resolve via each series' `dataKey`. */
export type FmPriceChartRow = {
  /** Category / x-axis key value (e.g. an ISO date). */
  date: string;
} & Record<string, number | Record<string, number> | undefined>;

/** A per-code sub-line (thin tint lines within a bucket). */
export interface FmPriceCodeSeries {
  /** Code key (e.g. assortment code). */
  code: string;
  /** Localised legend/tooltip name. */
  name: string;
  /** Resolved stroke colour (DS var token). */
  color: string;
}

/** An emphasis overlay line (average, rolling mean, secondary MA…). */
export interface FmPriceOverlaySeries {
  /** Row key this line reads (e.g. 'unweightedAvg', 'rolling3yr'). */
  dataKey: string;
  /** Localised legend/tooltip name. */
  name: string;
  /** Resolved stroke colour (DS var token). */
  color: string;
  /** Semantic overlay variant: 'dominant' | 'rolling' | 'secondary'. */
  variant?: keyof typeof OVERLAY_VARIANTS;
  /**
   * Line pattern overriding the variant's; `''` forces a solid line. Lets the
   * chart element give neighbouring lines distinct patterns.
   */
  dash?: string;
  /** Optional stable id forwarded to the Recharts <Line>. */
  id?: string;
}

/** A hidden, test-only mirror reporting whether a series has real points. */
export interface FmPriceSeriesMirror {
  'data-testid': string;
  hasPoints: boolean;
  pointCount: number;
}

export interface FmPriceChartProps {
  /** Chart rows (already shaped: date + numeric series + perCode map). */
  data: FmPriceChartRow[];
  /** Per-code sub-lines; each reads `row.perCode[code]`. */
  codeSeries?: FmPriceCodeSeries[];
  /** Emphasis overlay lines (average / rolling / secondary). */
  overlaySeries: FmPriceOverlaySeries[];
  /** Fires on brush drag with the selected [fromDate, toDate]. */
  onDateRangeChange?: (range: [string, string]) => void;
  /** X-axis tick formatter (owns any locale/date shortening). */
  formatXAxisTick: (value: string) => string;
  /** Y-axis tick formatter. */
  formatYAxisTick: (value: number) => string;
  /** Y-axis title. */
  yAxisLabel: string;
  /** Tooltip value formatter → returns the display string. */
  formatTooltipValue: (value: number | null) => string;
  /**
   * Direct end-of-line labels naming each overlay series and its last value
   * (unit-free; the axis title carries the unit). Leave off when a legend
   * already names the series, so identity is printed once.
   */
  endLabels?: boolean;
  /** Hidden test mirrors rendered before the plot. */
  seriesMirrors?: FmPriceSeriesMirror[];
  'data-testid'?: string;
}

function usePriceChartState({
  data,
  codeSeries,
  onDateRangeChange,
}: Pick<FmPriceChartProps, 'data' | 'codeSeries' | 'onDateRangeChange'>) {
  const handleBrushChange = useCallback(
    (brushData: { startIndex?: number; endIndex?: number }) => {
      const { startIndex, endIndex } = brushData;
      if (startIndex == null || endIndex == null || data.length === 0) return;
      const fromDate = data[startIndex]?.date;
      const toDate = data[endIndex]?.date;
      if (fromDate && toDate && onDateRangeChange) onDateRangeChange([fromDate, toDate]);
    },
    [data, onDateRangeChange],
  );
  const perCodeAccessors = useMemo(
    () =>
      (codeSeries ?? NO_CODE_SERIES).map((series) => ({
        ...series,
        accessor: (row: FmPriceChartRow) => {
          const perCode = row.perCode as Record<string, number> | undefined;
          return perCode ? perCode[series.code] : undefined;
        },
      })),
    [codeSeries],
  );
  const yearTicks = useMemo(() => {
    const firstDateByYear = new Map<string, string>();
    data.forEach((row) => {
      const year = /^\d{4}/.exec(row.date)?.[0];
      if (year && !firstDateByYear.has(year)) firstDateByYear.set(year, row.date);
    });
    return firstDateByYear.size >= 2 ? [...firstDateByYear.values()] : undefined;
  }, [data]);
  return { handleBrushChange, perCodeAccessors, yearTicks };
}

function lastFinitePoint(
  data: readonly FmPriceChartRow[],
  dataKey: string,
): { date: string; value: number } | null {
  for (let index = data.length - 1; index >= 0; index -= 1) {
    const value = data[index]?.[dataKey];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return { date: data[index].date, value };
    }
  }
  return null;
}

/**
 * Push stacked labels apart by the minimum gap, then, when the stack runs past
 * the plot bottom, slide it back up so no label prints over the x axis or
 * outside the plot (narrow phone plots stack three labels in little height).
 */
function spreadLabels(
  placed: { y: number }[],
  bounds: { top: number; bottom: number } | null,
): void {
  placed.forEach((label, index) => {
    const previous = placed[index - 1];
    if (previous && label.y - previous.y < END_LABEL_MIN_GAP) {
      label.y = previous.y + END_LABEL_MIN_GAP;
    }
  });
  if (!bounds || placed.length === 0) return;
  const overflow = placed[placed.length - 1].y - bounds.bottom;
  if (overflow > 0) for (const label of placed) label.y -= overflow;
  const underflow = bounds.top - placed[0].y;
  if (underflow > 0) for (const label of placed) label.y += underflow;
}

/**
 * End-of-line value labels drawn as one layer: they are stacked in value order
 * and pushed apart by a minimum gap, so near-equal series stay readable.
 */
const PriceEndLabels: React.FC<{
  data: readonly FmPriceChartRow[];
  series: readonly FmPriceOverlaySeries[];
  formatValue: (value: number) => string;
}> = ({ data, series, formatValue }) => {
  const xScale = useXAxisScale();
  const yScale = useYAxisScale();
  const plot = usePlotArea();
  if (!xScale || !yScale) return null;
  const placed = series
    .flatMap((entry) => {
      const point = lastFinitePoint(data, entry.dataKey);
      if (!point) return [];
      const x = xScale(point.date);
      const y = yScale(point.value);
      if (x === undefined || y === undefined || !Number.isFinite(x) || !Number.isFinite(y)) {
        return [];
      }
      return [{ entry, value: point.value, x, y: y - END_LABEL_OFFSET }];
    })
    .sort((a, b) => a.y - b.y);
  spreadLabels(
    placed,
    plot ? { top: plot.y + END_LABEL_MIN_GAP, bottom: plot.y + plot.height } : null,
  );
  return (
    <g className="fm-price-chart__end-labels">
      {placed.map(({ entry, value, x, y }) => (
        <text
          key={entry.dataKey}
          className="fm-price-chart__end-label"
          x={x - END_LABEL_OFFSET}
          y={y}
          textAnchor="end"
          fill={entry.color}
        >
          {entry.name} {formatValue(value)}
        </text>
      ))}
    </g>
  );
};

/**
 * Timber-price line chart. Data-driven, geometry-owned, colour-agnostic.
 */
export const FmPriceChart: React.FC<FmPriceChartProps> = ({
  data,
  codeSeries,
  overlaySeries,
  onDateRangeChange,
  formatXAxisTick,
  formatYAxisTick,
  yAxisLabel,
  formatTooltipValue,
  endLabels,
  seriesMirrors = [],
  'data-testid': testId,
}) => {
  const { handleBrushChange, perCodeAccessors, yearTicks } = usePriceChartState({
    data,
    codeSeries,
    onDateRangeChange,
  });

  const resolveVariantGeometry = (variant?: keyof typeof OVERLAY_VARIANTS) => {
    if (!variant || !OVERLAY_VARIANTS[variant]) {
      return OVERLAY_VARIANTS.dominant;
    }
    return OVERLAY_VARIANTS[variant];
  };

  return (
    <div className="fm-price-chart">
      {seriesMirrors.map((m) => (
        <span
          key={m['data-testid']}
          data-testid={m['data-testid']}
          data-has-points={m.hasPoints ? 'true' : 'false'}
          data-point-count={m.pointCount}
          hidden
        />
      ))}
      <ResponsiveContainer
        width="100%"
        height={CHART_HEIGHT}
        minWidth={1}
        minHeight={1}
        initialDimension={{ width: CHART_MAX_PLOT_WIDTH, height: CHART_HEIGHT }}
        data-testid={testId}
      >
        <LineChart
          key={`${data.length}:${String(data[0]?.date ?? '')}:${String(data[data.length - 1]?.date ?? '')}`}
          data={data}
          margin={{ top: 24, right: 16, bottom: 8, left: 0 }}
          accessibilityLayer
        >
          <CartesianGrid {...CHART_GRID_PROPS} />
          <XAxis
            {...CHART_XAXIS_PROPS}
            dataKey="date"
            ticks={yearTicks}
            tickFormatter={formatXAxisTick}
            interval="preserveStartEnd"
          />
          <YAxis
            {...CHART_YAXIS_PROPS}
            label={yAxisTitle(yAxisLabel)}
            tickFormatter={formatYAxisTick}
          />
          <RechartsTooltip content={<FmChartTooltip formatValue={formatTooltipValue} />} />

          {perCodeAccessors.map((s) => (
            <Line
              key={s.code}
              type="linear"
              dataKey={s.accessor}
              name={s.name}
              stroke={s.color}
              dot={false}
              strokeWidth={FM_PRICE_LINE_STROKE_WIDTH_WEEKLY}
              legendType="circle"
              isAnimationActive={false}
            />
          ))}

          {overlaySeries.map((s) => {
            const geometry = resolveVariantGeometry(s.variant);
            return (
              <Line
                key={s.dataKey}
                id={s.id}
                type="linear"
                dataKey={s.dataKey}
                name={s.name}
                stroke={s.color}
                strokeOpacity={geometry.strokeOpacity}
                dot={false}
                strokeWidth={geometry.strokeWidth}
                strokeDasharray={s.dash === undefined ? geometry.dashArray : s.dash || undefined}
                legendType="line"
                isAnimationActive={false}
              />
            );
          })}

          {endLabels && overlaySeries.length <= MAX_END_LABELS ? (
            <PriceEndLabels data={data} series={overlaySeries} formatValue={formatYAxisTick} />
          ) : null}

          {/*
            Recharts Brush does not forward custom data-testid attributes to its
            rendered SVG group; tests select via the stable `.recharts-brush`
            class (or `.recharts-brush-traveller` children) emitted by Recharts.
          */}
          {onDateRangeChange && data.length > 0 ? (
            <Brush
              dataKey="date"
              startIndex={0}
              endIndex={data.length - 1}
              ariaLabel={`${yAxisLabel}: ${String(data[0]?.date ?? '')}–${String(data[data.length - 1]?.date ?? '')}`}
              height={CHART_BRUSH_HEIGHT}
              onChange={handleBrushChange}
              travellerWidth={CHART_BRUSH_TRAVELLER_WIDTH}
            />
          ) : null}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default FmPriceChart;
